import { useEffect, useRef } from 'react';
import {
  openSettingsOverlay,
  openWalletOverlay,
  playDailyBonusOverlay,
  type OverlayBoardArt,
  type PlayerProfile,
  type SlotSettingsHost,
} from '@goodtime/engine';
import { claimLocally, computePreviewStreak, REWARD_FOR_DAY } from './dailyBonus';
import checkUrl from '../assets/icon/check.png';
import coinUrl from '../assets/icon/coin.png';
import gemUrl from '../assets/icon/gem.png';
import giftUrl from '../assets/icon/gift.png';
import bezelUrl from '../assets/road/light_bezel.png';
import floorUrl from '../assets/reel/forest_column_bg.png';
import { BUTTON_PLATES } from '../themes/roadGames';

/**
 * Settings, the wallet and the daily bonus on screens other than the slot
 * (the home screen, a wheel table): the same boards the slot draws on its
 * canvas, here on the overlay canvas over the screen (producer 2026-10-14:
 * everything in the game in Pixi). Each opens when mounted and tells its
 * host when it has closed; none renders anything itself.
 */

const ART: OverlayBoardArt = {
  floor: floorUrl,
  coin: coinUrl,
  gem: gemUrl,
  gift: giftUrl,
  check: checkUrl,
  bezel: bezelUrl,
  plates: BUTTON_PLATES,
};

/** Run `open` once on mount (once only, even when an effect runs twice),
 *  then `done` when it resolves, if still mounted. */
function useOpenOnce(open: () => Promise<void>, done: () => void): void {
  const latest = useRef({ open, done });
  latest.current = { open, done };
  const started = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    if (!started.current) {
      started.current = true;
      void latest.current.open().then(() => {
        if (mounted.current) latest.current.done();
      });
    }
    return () => {
      mounted.current = false;
    };
  }, []);
}

export function OverlaySettings({
  profile,
  host,
  onClose,
}: {
  profile: PlayerProfile;
  host: SlotSettingsHost;
  onClose: () => void;
}) {
  useOpenOnce(() => openSettingsOverlay(profile, host, ART), onClose);
  return null;
}

/** `reelGems`: gems land on the player's reels (reel_gems), which the board says. */
export function OverlayWallet({
  gems,
  coins,
  reelGems = false,
  onClose,
}: {
  gems: number;
  coins: number;
  reelGems?: boolean;
  onClose: () => void;
}) {
  useOpenOnce(() => openWalletOverlay(gems, coins, ART, reelGems), onClose);
  return null;
}

export function OverlayDailyBonus({
  profile,
  onClaim,
  target,
  onDone,
}: {
  profile: PlayerProfile;
  /** Claims on the server wallet; without it the local profile claims. */
  onClaim?: () => Promise<{ reward: number; streak: number } | null>;
  /** Where the coin counter is (screen pixels), for the coins to fly into. */
  target: () => { x: number; y: number } | null;
  onDone: () => void;
}) {
  const latest = useRef({ onClaim, target });
  latest.current = { onClaim, target };
  useOpenOnce(
    () =>
      playDailyBonusOverlay(
        {
          today: computePreviewStreak(profile),
          rewardFor: REWARD_FOR_DAY,
          claim: () => (latest.current.onClaim ? latest.current.onClaim() : Promise.resolve(claimLocally(profile))),
        },
        ART,
        () => latest.current.target(),
      ),
    onDone,
  );
  return null;
}
